'use client';

import { useEffect, useState, type FormEvent } from 'react';
import { LoginDrawer } from '@/platform/auth/LoginDrawer';
import { createCalendarEvent, deleteCalendarEvent, submitCalendarEventRequest, updateCalendarEvent } from './api';
import { CalendarEventForm } from './CalendarEventForm';
import { draftFromEvent, draftToApiInput } from './eventUtils';
import type { CalendarEvent, CalendarEventDraft } from './types';

type Props = {
  open: boolean;
  event: CalendarEvent | null;
  canEdit: boolean;
  requestMode?: boolean;
  isAuthenticated?: boolean;
  initialDraft?: Partial<CalendarEventDraft>;
  onClose: () => void;
  onSaved: () => void;
};

export function CalendarEventModal({
  open,
  event,
  canEdit,
  requestMode = false,
  isAuthenticated = false,
  initialDraft,
  onClose,
  onSaved,
}: Props) {
  const isCreate = !event;
  const readOnly = !canEdit && !requestMode;
  const [draft, setDraft] = useState<CalendarEventDraft>(() => draftFromEvent(event));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const base = draftFromEvent(event);
    setDraft({ ...base, ...initialDraft });
    setError(null);
  }, [open, event, initialDraft]);

  if (!open) return null;

  async function save(e?: FormEvent) {
    e?.preventDefault();
    if (readOnly) return;
    if (requestMode && !isAuthenticated) return;
    if (!draft.title.trim()) {
      setError('Title is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = draftToApiInput(draft);
      if (requestMode) {
        await submitCalendarEventRequest(body);
      } else if (isCreate) {
        await createCalendarEvent(body);
      } else if (event) {
        await updateCalendarEvent(event.id, body);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (readOnly || !event) return;
    if (!window.confirm('Delete this event?')) return;
    setSaving(true);
    setError(null);
    try {
      await deleteCalendarEvent(event.id);
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="theme-modal theme-modal--open calendar-event-modal"
      role="dialog"
      aria-label={isCreate ? 'Create event' : event?.title ?? 'Event'}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="theme-modal__card calendar-event-modal__card">
        <h2>
          {requestMode
            ? 'Suggest an event'
            : isCreate
              ? 'Create event'
              : readOnly
                ? 'Event details'
                : 'Edit event'}
        </h2>
        {requestMode && (
          <p className="text-muted">
            Add a community event to the calendar. Submissions are reviewed before they appear publicly.
          </p>
        )}

        {requestMode && !isAuthenticated ? (
          <>
            <LoginDrawer />
            <div className="calendar-event-modal__actions">
              <span className="calendar-event-modal__actions-spacer" />
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
            </div>
          </>
        ) : (
          <form
            className="calendar-event-form-wrap"
            onSubmit={(e) => void save(e)}
          >
            <CalendarEventForm draft={draft} readOnly={readOnly} onChange={setDraft} suggestMode={requestMode} />
            {error && <p className="form-error">{error}</p>}
            <div className="calendar-event-modal__actions">
              {!readOnly && !isCreate && (
                <button type="button" className="btn btn-secondary" disabled={saving} onClick={remove}>
                  Delete
                </button>
              )}
              <span className="calendar-event-modal__actions-spacer" />
              <button type="button" className="btn btn-secondary" disabled={saving} onClick={onClose}>
                Cancel
              </button>
              {!readOnly && (
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : requestMode ? 'Submit for review' : 'Save'}
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
